import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { message, Modal } from "antd";
import useOptions from "features/masterData/hooks/useOptions";
import { t } from "i18next";
import { generateRandomString, sortDays } from "utils/helper";
import { DEFAULT_SHIFTPERIOD } from "../schemas/shiftsSchema";
import * as services from "../services/shiftsApi";
import {
  setCurrentShifts,
  setIsLoading,
  setShiftsList,
} from "../slices/attendanceSlice";

function toInitialValues(
  initialValues = {},
  weekDayWorkingOptions = [],
  weekWorkingOptions = []
) {
  const {
    attendanceCondition = {},
    checkInChanel = {},
    doubleShiftOne = {},
    doubleShiftTwo = {},
    doubleShiftThree = {},
    shiftPeriods = [],
    ...rest
  } = initialValues;
  const weekday =
    weekDayWorkingOptions.find((w) => w.nameEn === "Week Day") || {};
  const option = weekWorkingOptions.find((o) => o.nameEn === "All Weeks") || {};

  const firstShift = shiftPeriods[0] || DEFAULT_SHIFTPERIOD;
  // eslint-disable-next-line no-unused-vars
  const { optionId = {}, workingDay = {}, ...fShift } = firstShift;

  const normalShifts = [
    {
      ...fShift,
      activateDeactivate: firstShift.activateDeactivate === "Y",
      workingDayId: weekday?.id || null,
      optionId: (optionId || {})?.id || option?.id || null,
    },
  ];

  const advanceShifts = shiftPeriods.map((shift = {}) => {
    const { workingDay = {}, optionId = {}, ...restShift } = shift;
    return {
      ...restShift,
      activateDeactivate: shift.activateDeactivate === "Y",
      workingDayId: workingDay?.id || null,
      optionId: optionId?.id || null,
    };
  });

  const modifiedValues = {
    ...rest,
    normalShifts,
    advanceShifts,
    attendanceConditionId: attendanceCondition?.id || null,
    checkInChanelId: checkInChanel?.id || null,
    doubleShift: initialValues?.doubleShift === "Y",
    doubleShiftOneId: doubleShiftOne?.id || null,
    doubleShiftTwoId: doubleShiftTwo?.id || null,
    doubleShiftThreeId: doubleShiftThree?.id || null,
    nightShift: initialValues?.nightShift === "Y",
    advanceSetting: initialValues?.advanceSetting === "Y",
    preShiftOtPeriod: initialValues?.preShiftOtPeriod === "Y",
    allowFlexibleWorkInStatus: initialValues?.allowFlexibleWorkInStatus === "Y",
    calculateOtAuto: initialValues?.calculateOtAuto === "Y",
    openAllowanceTab: initialValues?.openAllowanceTab === "Y",
    specialShiftFromOt: initialValues?.specialShiftFromOt === "Y",
    specialShiftFromHoliday: initialValues?.specialShiftFromHoliday === "Y",
    workTimeAllowance: initialValues?.workTimeAllowance === "Y",
    lateStatusDuringBreaksCalculation:
      initialValues?.lateStatusDuringBreaksCalculation === "Y",
  };
  return modifiedValues;
}

function toParams(values = {}, weekDayWorkingOptions = []) {
  const {
    normalShifts = [],
    advanceShifts = [],
    // eslint-disable-next-line no-unused-vars
    company = {},
    ...rest
  } = values;
  let modifiedShiftPeriods = [];
  if (!values.advanceSetting) {
    modifiedShiftPeriods = weekDayWorkingOptions
      .filter((o) => o.nameEn !== "Week Day")
      .sort(sortDays)
      .map((o) => ({
        ...normalShifts[0],
        workingDayId: o?.id,
      }));
  } else {
    modifiedShiftPeriods = [...advanceShifts];
  }
  const modifiedParams = {
    ...rest,
    attendanceSettingShiftId: values?.id,
    doubleShift: values.doubleShift ? "Y" : "N",
    nightShift: values.nightShift ? "Y" : "N",
    advanceSetting: values.advanceSetting ? "Y" : "N",
    shiftPeriods: modifiedShiftPeriods.map((s) => ({
      ...s,
      activateDeactivate: s.activateDeactivate ? "Y" : "N",
    })),
    preShiftOtPeriod: values.preShiftOtPeriod ? "Y" : "N",
    allowFlexibleWorkInStatus: values.allowFlexibleWorkInStatus ? "Y" : "N",
    calculateOtAuto: values.calculateOtAuto ? "Y" : "N",
    checkInChanelId: values.calculateOtAuto ? values.checkInChanelId : null,
    openAllowanceTab: values.openAllowanceTab ? "Y" : "N",
    specialShiftFromOt: values.specialShiftFromOt ? "Y" : "N",
    specialShiftFromHoliday: values.specialShiftFromHoliday ? "Y" : "N",
    workTimeAllowance: values.workTimeAllowance ? "Y" : "N",
    lateStatusDuringBreaksCalculation: values.lateStatusDuringBreaksCalculation
      ? "Y"
      : "N",
  };
  return modifiedParams;
}

function useShiftsEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.attendance.isLoading);
  const shiftsList = useSelector((state) => state.attendance.shiftsList);
  const currentShifts = useSelector((state) => state.attendance.currentShifts);

  const {
    attendanceConditionOptions = [],
    checkInChanelOptions = [],
    weekDayWorkingOptions = [],
    weekWorkingOptions = [],
    getAttendanceConditionOptions,
    getCheckInChanelOptions,
    getWeekDayWorkingOptions,
    getWeekWorkingOptions,
  } = useOptions();

  const initialValues = useMemo(() => {
    return toInitialValues(
      currentShifts,
      weekDayWorkingOptions,
      weekWorkingOptions
    );
  }, [currentShifts, weekDayWorkingOptions, weekWorkingOptions]);

  const modifiedShiftsList = useMemo(() => {
    const modifiedOptions = shiftsList.map((opt) => ({
      label:
        i18n.language === "en"
          ? opt.shiftNameEn
          : opt.shiftName,
      value: opt?.id,
    }));
    return modifiedOptions;
  }, [i18n.language, shiftsList]);

  const getAttendanceShifts = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getAttendanceShiftsPaging({
        requestId: generateRandomString(),
        companyId: currentCompany,
        page: 0,
        size: 100,
      });
      dispatch(setShiftsList(response.data?.attendanceShiftResp || []));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);

  const getAttendanceShiftsById = useCallback(
    async (attendanceSettingShiftId = "") => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getAttendanceShiftsById({
          attendanceSettingShiftId,
          requestId: generateRandomString(),
          companyId: currentCompany,
        });
        dispatch(setCurrentShifts(response.data || {}));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const onCancel = useCallback(() => {
    navigate("/configuration/attendance", { state: { selectedTab: 'shifts' } });
  }, [navigate]);

  const updateAttendanceShifts = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        let result = await services.updateAttendanceShifts(
          toParams(values, weekDayWorkingOptions)
        );
        message.success(t("common.success"))
        onCancel();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [weekDayWorkingOptions, dispatch, onCancel]
  );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = { ...values, companyId: currentCompany };
      Modal.confirm({
        title: t('generalInfo.confirmation'),
        content: (
          <>
            <p>{t('generaInfo.are-you-sure-confirm-to-edit')}</p>
          </>
        ),
        onOk() {
          updateAttendanceShifts(modifiedValues);
        },
        okText: t('confirm'),
        cancelText: t('generalInfo.cancel'),
      });
    },
    [currentCompany, updateAttendanceShifts]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object" && params?.id) {
      getAttendanceShiftsById(params?.id);
    }
  }, [currentCompany, params?.id, getAttendanceShiftsById]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getAttendanceShifts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  useEffect(() => {
    getAttendanceConditionOptions();
  }, [getAttendanceConditionOptions]);

  useEffect(() => {
    getCheckInChanelOptions();
  }, [getCheckInChanelOptions]);

  useEffect(() => {
    getWeekDayWorkingOptions();
  }, [getWeekDayWorkingOptions]);

  useEffect(() => {
    getWeekWorkingOptions();
  }, [getWeekWorkingOptions]);

  return {
    isLoading,
    initialValues,
    shiftsList: modifiedShiftsList,
    attendanceConditionOptions,
    checkInChanelOptions,
    weekDayWorkingOptions,
    weekWorkingOptions,
    onCancel,
    onSubmit,
  };
}

export default useShiftsEdit;
